import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { map, mergeMap, exhaustMap, catchError, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { SocketService } from '../../services/socket.service';
import * as AuthActions from './auth.actions';
import { AuthService } from '../../services/auth.service';
import { TokenService } from '../../services/token.service';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { RedirectService } from '../../../core/services/redirect.service';
import { isSupportedLanguage } from '../../services/translation.service';
import { localizedURL } from '../../helpers/navigation.helper';
import { StorageService } from '../../services/storage.service';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private tokenService = inject(TokenService);
  private router = inject(Router);
  private socialAuthService = inject(SocialAuthService);
  private redirectService = inject(RedirectService);
  private socketService = inject(SocketService)

  /** Turn an HTTP error into a user-friendly message */
  private handleError(error: HttpErrorResponse): string {
    return error.error?.message || 'An unexpected error occurred';
  }

  constructor(
    private storageService: StorageService
  ) { }

  /**
   * On app init, decide:
   *  - no tokens      → noStoredToken
   *  - valid access   → getCurrentUserSuccess
   *  - expired access + valid refresh → refresh → save tokens → getCurrentUserSuccess
   *  - otherwise      → clear & noStoredToken
   */
  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.initAuth),
      exhaustMap(() => {
        const hasA = this.tokenService.hasAccessToken();
        const hasR = this.tokenService.hasRefreshToken();
        const expired = this.tokenService.isAccessTokenExpired();

        // nothing stored
        if (!hasA && !hasR) {
          return of(AuthActions.noStoredToken());
        }

        // access valid
        if (hasA && !expired) {
          return this.authService.getCurrentUser().pipe(
            mergeMap(user => [
              AuthActions.setUserHints({
                userHints: {
                  hintsClosed: user.hintsClosed,
                  showHints: user.showHints
                }
              }),
              AuthActions.setUserBalance({ balance: user.balance }),
              AuthActions.setUserAIJob({ aiJob: user.aiJob }),
              AuthActions.getCurrentUserSuccess({ user })
            ]),
            catchError(() => {
              this.tokenService.removeTokens();
              return of(AuthActions.noStoredToken());
            })
          );
        }

        // expired access + have refresh → try refresh
        return this.authService.refreshToken().pipe(
          // save new tokens before proceeding
          tap(res => {
            this.tokenService.setTokens(res.accessToken, res.refreshToken);
            this.socketService.updateAuthToken(res.accessToken);
          }
          ),
          mergeMap(() =>
            this.authService.getCurrentUser().pipe(
              mergeMap(user => [
                AuthActions.setUserHints({
                  userHints: {
                    hintsClosed: user.hintsClosed,
                    showHints: user.showHints
                  }
                }),
                AuthActions.setUserBalance({ balance: user.balance }),
                AuthActions.setUserAIJob({ aiJob: user.aiJob }),
                AuthActions.getCurrentUserSuccess({ user })
              ]),
              catchError(userErr =>
                of(AuthActions.getCurrentUserFailure({ error: this.handleError(userErr) }))
              )
            )
          ),
          catchError(() => {
            // refresh failed → clear all
            this.tokenService.removeTokens();
            return of(AuthActions.noStoredToken());
          })
        );
      })
    )
  );

  /** User-initiated login */
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      exhaustMap(({ credentials }) =>
        this.authService.login(credentials).pipe(
          // on success, persist tokens
          tap(res => {
            this.tokenService.setTokens(res.accessToken, res.refreshToken);
            this.socketService.updateAuthToken(res.accessToken);
          }),
          // dispatch multiple actions: hints, balance, loginSuccess
          mergeMap(resp => [
            AuthActions.setUserHints({
              userHints: {
                hintsClosed: resp.user.hintsClosed,
                showHints: resp.user.showHints
              }
            }),
            AuthActions.setUserBalance({ balance: resp.user.balance }),
            AuthActions.setUserAIJob({ aiJob: resp.user.aiJob }),
            AuthActions.loginSuccess({
              user: resp.user,
              accessToken: resp.accessToken,
              refreshToken: resp.refreshToken,
              socialRegistered: resp.socialRegistered,
              socialMerged: resp.socialMerged,
              socialLogged: resp.socialLogged
            })
          ]),
          catchError((err: HttpErrorResponse) =>
            of(AuthActions.loginFailure({ error: this.handleError(err) }))
          )
        )
      )
    )
  );

  /** (Optional) Navigate after loginSuccess */
  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(() => {
          if (!this.redirectService.hasRedirectUrl()) {
            const savedLang = this.storageService.getLanguage(true);
            const lang = savedLang && isSupportedLanguage(savedLang) ? savedLang : 'en';
            const playareaUrl = localizedURL('/playarea', lang);
            this.router.navigate([playareaUrl]);
          }
        })
      ),
    { dispatch: false }
  );

  /** User-initiated registration */
  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.register),
      exhaustMap(({ credentials }) =>
        this.authService.register(credentials).pipe(
          tap(res => {
            this.tokenService.setTokens(res.accessToken, res.refreshToken);
            this.socketService.updateAuthToken(res.accessToken);
          }),
          mergeMap(resp => [
            AuthActions.setUserHints({
              userHints: {
                hintsClosed: resp.user.hintsClosed,
                showHints: resp.user.showHints
              }
            }),
            AuthActions.setUserBalance({ balance: resp.user.balance }),
            AuthActions.setUserAIJob({ aiJob: resp.user.aiJob }),
            AuthActions.registerSuccess({
              user: resp.user,
              accessToken: resp.accessToken,
              refreshToken: resp.refreshToken
            })
          ]),
          catchError((err: HttpErrorResponse) =>
            of(AuthActions.registerFailure({ error: this.handleError(err) }))
          )
        )
      )
    )
  );

  /** Navigate after successful registration */
  registerSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.registerSuccess),
        tap(() => {
          if (!this.redirectService.hasRedirectUrl()) {
            const savedLang = this.storageService.getLanguage(true);
            const lang = savedLang && isSupportedLanguage(savedLang) ? savedLang : 'en';
            const playareaUrl = localizedURL('/playarea', lang);
            this.router.navigate([playareaUrl]);
          }
        })
      ),
    { dispatch: false }
  );

  /** Logout: clear tokens, social sign-out, navigate, then emit success */
  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      tap(() => {
        this.tokenService.removeTokens();
        this.socialAuthService.signOut();
        const savedLang = this.storageService.getLanguage(true);
        const lang = savedLang && isSupportedLanguage(savedLang) ? savedLang : 'en';
        const loginUrl = localizedURL('/auth/login', lang);
        this.router.navigate([loginUrl]);
      }),
      map(() => AuthActions.logoutSuccess())
    )
  );

  /** Drop socket on logout */
  disconnectSocketOnLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap(() => {
          try {
            this.socketService.leave(`aiq:user:*` as any); // optional; leave known rooms if you track them
          } catch { }
          this.socketService.disconnect();
        })
      ),
    { dispatch: false }
  );

  /** Fetch current user on demand */
  getCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getCurrentUser),
      exhaustMap(() =>
        this.authService.getCurrentUser().pipe(
          mergeMap(user => [
            AuthActions.setUserHints({
              userHints: {
                hintsClosed: user.hintsClosed,
                showHints: user.showHints
              }
            }),
            AuthActions.setUserBalance({ balance: user.balance }),
            AuthActions.setUserAIJob({ aiJob: user.aiJob }),
            AuthActions.getCurrentUserSuccess({ user })
          ]),
          catchError((err: HttpErrorResponse) => {
            this.tokenService.removeTokens();
            return of(AuthActions.getCurrentUserFailure({ error: this.handleError(err) }));
          })
        )
      )
    )
  );

  /** After we know the user, connect socket and join user room */
  connectSocketOnAuth$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AuthActions.getCurrentUserSuccess,
          AuthActions.loginSuccess,
          AuthActions.registerSuccess
        ),
        tap(({ user }) => {
          // 1) ensure the socket is up
          this.socketService.connect();
          // 2) join the user-wide room so we get aiq updates on ANY page
          this.socketService.join(`aiq:user:${user.id}`);
          console.log('connectSocketOnAuth$ User room joined:', `aiq:user:${user.id}`);
        })
      ),
    { dispatch: false }
  );
}
